// core
import { Injectable } from '@angular/core';

// libraries
import tinycolor from 'tinycolor2';

@Injectable({
  providedIn: 'root'
})
export class GamutPaletteService {
  palettes?: any;

  constructor() {
    this.palettes = [];
  }

  /**
   * @function isValid
   * @memberof GamutPaletteSerivce
   * @summary Returns whether color string is valid
   * @example
   * gamutPaletteService.isValid("red"); //returns true
   * gamutPaletteService.isValid("#ff0000"); //returns true
   * gamutPaletteService.isValid(""); //returns false
   * @param {tinycolor.ColorInput} color Color value, used as a string
   * @returns {boolean} True if color is valid, otherwise false
   * @description Wrapper function for tinycolor(color).isValid()
   */
  isValid(color: tinycolor.ColorInput): boolean {
    return tinycolor(color).isValid();
  }

  /**
   * @function getColorNames
   * @memberof GamutPaletteService
   * @summary Get a list of all named web colours
   * @example
   * var firstColorName = GamutPaletteService.getColorNames()['aliceblue']; //returns "f0f8ff"
   * @returns {object} Key/pair list of web colour names
   * @description Returns an object list of all web colour names
   */
  getColorNames(): object {
    return tinycolor.names;
  }

  /**
   * @function multiplyColor
   * @memberof GamutPaletteService
   * @summary Mixes two rgb colour objects through multiplication of values
   * @example TODO
   * @param {any} rgb1 tinycolor rbg color value object
   * @param {any} rgb2 tinycolor rbg color value object
   * @returns {tinycolor.Instance} Instance of the tinycolor object
   * @description Creates a tinycolor object by multiplying two rgb values together and passes them into the tinycolor constructor.
   * @todo This is currently redundant using the current palette generation algorithm.
   */
  multiplyColor(rgb1: any, rgb2: any): tinycolor.Instance {
    return tinycolor([
      'rgb',
      Math.floor(rgb1.b * rgb2.b / 255),
      Math.floor(rgb1.g * rgb2.g / 255),
      Math.floor(rgb1.r * rgb2.r / 255)
    ].join(' '));
	};

  /**
   * @function getHexString
   * @memberof GamutPaletteSerivce
   * @summary Gets the HEX value of a colour
   * @example
   * var colorHex = gamutPaletteSerivce.getHexString('red'); //returns '#FF0000'
   * @param {tinycolor.ColorInput} color tinycolor color input value
   * @returns {string} HEX value of color
   * @description Wrapper function for tinycolor(color).toHexString(), takes any valid tinycolor input value and returns it as a HEX string.
   */
  getHexString(color: tinycolor.ColorInput): string {
    return tinycolor(color).toHexString();
  }

  /**
   * @function getIsLight
   * @memberof GamutPaletteSerivce
   * @summary Determines wehther or not the color is contrasted against white or black
   * @example
   * var whiteTextContrast = gamutPaletteSerivce.getIsLight('white'); //returns true
   * var blackTextContrast = gamutPaletteSerivce.getHexString('black'); //returns false
   * @param {tinycolor.ColorInput} color tinycolor color input value
   * @returns {boolean} True if input color is light and contrasts against black, otherwise false
   * @description Wrapper function for tinycolor(color).isLight(),
   */
  getIsLight(color: tinycolor.ColorInput): boolean {
    return tinycolor(color).isLight();
  }

  /**
   * @function getColorObject
   * @memberof GamutPaletteSerivce
   * @summary Gets a color property set used to generate palettes
   * @example
   * var deepRed = this.getColorObject("#990000", "Deep Red");
   * //Returns
   * {
   *  name: "Deep Red",
   *  baseColor: "#990000",
   *  darkContrast: false
   * }
   * @param {tinycolor.ColorInput} color tinycolor color input value
   * @param {string} name Name of the color value as a string
   * @returns {object} Property set for a color
   * @description Data transform function used to generate color values used in the building of palettes
   */
  getColorObject(color: tinycolor.ColorInput, name: string): object {
		const tc = tinycolor(color);
		return {
			name: name,
			baseColor: tc.toHexString(),
			darkContrast: tc.isLight()
		};
	}

  /**
   * @function generatePalette
   * @memberof GamutPaletteSerivce
   * @summary GFenerattes a colour palette from the supplied dates.
   * @example
   * @param {any} baseColor The tinycolor valid color value from which to build the palette.
   * @returns {array} Array of colour objects from getColorObject()
   * @description This method is a custom alorithm that find the mid palette colour and mixes white on the light end and darkens on the dark end.
   * If the base colour is not inside the 40-60% light range to be a valid mid-range colour then a swapIntoPalette() call should be made to see if it can be added and a valid slot.
   * Note, this algorithm is buiild for Open branding(old) but has been testing against all value derived from getColorNames()
   */
  generatePalette(baseColor: any) {
    // Keep colour if it is in the primary light threshold
    const primaryHsl = tinycolor(baseColor).toHsl();
    const baseLight = tinycolor('#ffffff');

    //If source brightness is not within %40-%60, set palette mid-range to %50
    if (primaryHsl.l < 0.4 || primaryHsl.l > 0.6) {
      primaryHsl.l = 0.5;
    }

    return [
      this.getColorObject(tinycolor.mix(baseLight, primaryHsl, 5), '25'),
      this.getColorObject(tinycolor.mix(baseLight, primaryHsl, 12), '50'),
      this.getColorObject(tinycolor.mix(baseLight, primaryHsl, 24), '100'),
      this.getColorObject(tinycolor.mix(baseLight, primaryHsl, 48), '200'),
      this.getColorObject(tinycolor.mix(baseLight, primaryHsl, 64), '300'),
      this.getColorObject(tinycolor.mix(baseLight, primaryHsl, 80), '400'),
      this.getColorObject(tinycolor(primaryHsl), '500'),
      this.getColorObject(tinycolor(primaryHsl).darken(4), '600'),
      this.getColorObject(tinycolor(primaryHsl).darken(9), '700'),
      this.getColorObject(tinycolor(primaryHsl).darken(15), '800'),
      this.getColorObject(tinycolor(primaryHsl).darken(22), '900')
    ];
  }

  /**
   * @function swapIntoPalette
   * @memberof GamutPaletteSerivce
   * @summary Swaps a colour into a generated palette object array if it can occupy a valid threshold on the 25, 100, 800 and 900 positions.
   * @example
   * @param {any} swapColor Colour to swap into palette
   * @param {any} palette Palette as an array of objects to swap colour into
   * @returns {array} returns the un/modified palette object
   * @description The swap-in thresholds are only done to meet the Open brand requirements for the design system.
   * Threshold should be determined for all values on the palette being used in the future should requirements change.
   * @todo I will be a good idea n the future to add more palette slots to add the colour.
   * @todo Angular+Typescript forces the reference of palette, this function doesn't need to return any value
   */
  swapIntoPalette(swapColor: any, palette: any) {
    var swappedPalette = palette;
    let light = tinycolor(swapColor).toHsl().l;

    for (let i = 0; i < swappedPalette.length; i++) {
      if (swappedPalette[i]["name"] === '25' && light > 0.96 && light < 0.98) {
        swappedPalette[i] = this.getColorObject(swapColor, '25');
      }
      if (swappedPalette[i]["name"] === '100' && light > 0.82 && light < 0.91) {
        swappedPalette[i] = this.getColorObject(swapColor, '100');
      }
      if (swappedPalette[i]["name"] === '800' && light > 0.33 && light < 0.37) {
        swappedPalette[i] = this.getColorObject(swapColor, '800');
      }
      if (swappedPalette[i]["name"] === '900' && light > 0.25 && light < 0.31) {
        swappedPalette[i] = this.getColorObject(swapColor, '900');
      }
    }

    return swappedPalette;
  }

  /**
   * @function mapBrandedStyleVariables
   * @memberof GamutPaletteSerivce
   * @summary Builds a style variable map from partner branding data
   * @example
   * var partnerBrand = {
   *  primaryColour: "red",
   *  secondaryColour: "blue"
   * };
   * var brandVariables = gamutPaletteSerivce.mapBrandedStyleVariables(partnerBrand);
   * //Return values
   * [
   *  {key: "--theme-action-primary", value: "#ff0000"}
   *  {key: "--theme-action-primary-disabled", value: "#ffc2c2"}
   *  {key: "--theme-action-primary-hovered", value: "#b30000"}
   *  {key: "--theme-action-primary-pressed", value: "#8f0000"}
   *  {key: "--theme-action-secondary", value: "#0000ff"}
   *  {key: "--theme-action-secondary-disabled", value: "#c2c2ff"}
   *  {key: "--theme-action-secondary-hovered", value: "#0000b3"}
   *  {key: "--theme-action-secondary-pressed", value: "#00008f"}
   *  {key: "--theme-surface-highlight", value: "#f2f2ff"}
   * ];
   * @param {any} partnerBrandData Patner branding values as an object from the api
   * @returns {array} List of colours objects that make up the generated palettes
   * @description This function takes the partner branding data and generates two palettes from the primary and secondary colours.
   * It then takes these two palettes and converts them to branding style variables.
   * @todo We may generate three palettes with version two of the partner branding.
   */
  mapBrandedStyleVariables(partnerBrandData: any) {
    var styleVariables = [];

    if (typeof partnerBrandData != "undefined") {
      var primaryPalette;
      var secondaryPalette;

      if (typeof partnerBrandData.primaryColour != "undefined") {
        primaryPalette = this.generatePalette(partnerBrandData.primaryColour);
        primaryPalette = this.swapIntoPalette(partnerBrandData.primaryColour, primaryPalette);
      }
      if (typeof partnerBrandData.secondaryColour != "undefined") {
        secondaryPalette = this.generatePalette(partnerBrandData.secondaryColour);
        secondaryPalette = this.swapIntoPalette(partnerBrandData.secondaryColour, secondaryPalette);
      }

      if (primaryPalette || secondaryPalette) {
        if (primaryPalette) {
          styleVariables.push({key: '--theme-action-primary', value: primaryPalette[6]["baseColor"]});
          styleVariables.push({key: '--theme-action-primary-disabled', value: primaryPalette[2]["baseColor"]});
          styleVariables.push({key: '--theme-action-primary-hovered', value: primaryPalette[9]["baseColor"]});
          styleVariables.push({key: '--theme-action-primary-pressed', value: primaryPalette[10]["baseColor"]});
        }
        if (secondaryPalette) {
          styleVariables.push({key: '--theme-action-secondary', value: secondaryPalette[6]["baseColor"]});
          styleVariables.push({key: '--theme-action-secondary-disabled', value: secondaryPalette[2]["baseColor"]});
          styleVariables.push({key: '--theme-action-secondary-hovered', value: secondaryPalette[9]["baseColor"]});
          styleVariables.push({key: '--theme-action-secondary-pressed', value: secondaryPalette[10]["baseColor"]});
          styleVariables.push({key: '--theme-surface-highlight', value: secondaryPalette[0]["baseColor"]});
        }
      }
    }

    return styleVariables;
  }

  //This function needs to be deprecated. Not going to document it because it is going away.
  createBrandedStyleVariables(partnerBrandData: any) {
    var styleVariables = "";

    if (typeof partnerBrandData != "undefined") {
      var primaryPalette;
      var secondaryPalette;

      if (typeof partnerBrandData.primaryColour != "undefined") {
        primaryPalette = this.generatePalette(partnerBrandData.primaryColour);
        primaryPalette = this.swapIntoPalette(partnerBrandData.primaryColour, primaryPalette);
      }
      if (typeof partnerBrandData.secondaryColour != "undefined") {
        secondaryPalette = this.generatePalette(partnerBrandData.secondaryColour);
        secondaryPalette = this.swapIntoPalette(partnerBrandData.secondaryColour, secondaryPalette);
      }

      if (primaryPalette || secondaryPalette) {
        if (primaryPalette) {
          styleVariables +=  `
  --theme-action-primary: `+primaryPalette[6]["baseColor"]+`;
  --theme-action-primary-disabled: `+primaryPalette[2]["baseColor"]+`;
  --theme-action-primary-hovered: `+primaryPalette[9]["baseColor"]+`;
  --theme-action-primary-pressed: `+primaryPalette[10]["baseColor"]+`;`;
        }
        if (secondaryPalette) {
          styleVariables += `
  --theme-action-secondary: `+secondaryPalette[6]["baseColor"]+`;
  --theme-action-secondary-disabled: `+secondaryPalette[2]["baseColor"]+`;
  --theme-action-secondary-hovered: `+secondaryPalette[9]["baseColor"]+`;
  --theme-action-secondary-pressed: `+secondaryPalette[10]["baseColor"]+`;
  --theme-surface-highlight: `+secondaryPalette[0]["baseColor"]+`;`;
        }
      }
    }

    return styleVariables;
  }

  /**
   * @function generateAllNamedPalettes
   * @memberof GamutPaletteService
   * @summary Generates a list of palettes from all named web colours
   * @example
   * var webColorPalettes = gamutPaletteService.generateAllNamedPalettes();
   * @returns {void} List of palettes to feed into the GamutPaletteComponent
   * @description This is a testing function to generate a lot of palettes to guide the developer in tweaking the algorithm values.
   */
  generateAllNamedPalettes() {
    var count = 0;
    for (const name in this.getColorNames()) {
      this.palettes[count] = this.generatePalette(name);
      if (name.match(/^[a-zA-Z]/g) === null) {
        this.palettes[count]["name"] = this.getHexString(name);
      } else {
        this.palettes[count]["name"] = name;
      }
      this.palettes[count]["title"] = this.getIsLight(name);
      count++;

      var newPalette = this.generatePalette(name);
      this.palettes[count] = this.swapIntoPalette(name, newPalette);
      this.palettes[count]["name"] = this.getHexString(name);
      this.palettes[count]["title"] = this.getIsLight(name);
      count++;
    }
  }

  /**
   * @function buildPalette
   * @memberof GamutPaletteService
   * @summary Builds a colour palette object from a colour value
   * @example
   * var paletteOne = gamutPaletteService.buildPalette("red");
   * var paletteTwo = gamutPaletteService.buildPalette("#ff0000");
   * //Both return the following object
   * {
   *   "name": "#ff0000",
   *   "title": false,
   *   "values": [{
   *     "name": "25",
   *     "baseColor": "#fff2f2",
   *     "darkContrast": true
   *   }, {
   *     "name": "50",
   *     "baseColor": "#ffe0e0",
   *     "darkContrast": true
   *   }, {
   *     "name": "100",
   *     "baseColor": "#ffc2c2",
   *     "darkContrast": true
   *   }, {
   *     "name": "200",
   *     "baseColor": "#ff8585",
   *     "darkContrast": true
   *   }, {
   *     "name": "300",
   *     "baseColor": "#ff5c5c",
   *     "darkContrast": true
   *   }, {
   *     "name": "400",
   *     "baseColor": "#ff3333",
   *     "darkContrast": false
   *   }, {
   *     "name": "500",
   *     "baseColor": "#ff0000",
   *     "darkContrast": false
   *   }, {
   *     "name": "600",
   *     "baseColor": "#eb0000",
   *     "darkContrast": false
   *   }, {
   *     "name": "700",
   *     "baseColor": "#d10000",
   *     "darkContrast": false
   *   }, {
   *     "name": "800",
   *     "baseColor": "#b30000",
   *     "darkContrast": false
   *   }, {
   *     "name": "900",
   *     "baseColor": "#8f0000",
   *     "darkContrast": false
   *   }]
   * }
   * @param {any} color Colour name or hexidecimal value as a string.
   * @returns {object} Colour palette object with contrast information added. If colour is invalid an empty palette object s returned with an empty string for the palette.name property.
   * @description This function builds color palettes from the tinycolor library returning each colour on the palette as a tinycolor colour value objects. Includes palette name and contrast for display.
   */
  buildPalette(color: any): object {
    var palette = {
      name: <string>'',
      title: <boolean>true,
      values: <any>[]
    };

    if (this.isValid(color)) {
      var newPalette = this.generatePalette(color);
      palette.values = this.swapIntoPalette(color, newPalette);
      palette.name = this.getHexString(color);
      palette.title = this.getIsLight(color);
    }

    return palette;
  }

  /**
   * @function getPaletteColor
   * @memberof GamutPaletteService
   * @summary Gets the baseColor value for a name value from a palette
   * @example
   * var palette = service.buildPalette("red");
   * var colour = service.getPaletteColor(palette, "500");
   * //Returns "#ff0000"
   * @param {any} palette Colour palette object returned from buildPalette()
   * @param {string} shade Name of the colour value in palette
   * @returns {string} Hexidecimal colour value as a string
   * @description This is a utility function for buildPalette to extract a colour value from the return object
   */
  getPaletteColor(palette: any, shade: string) {
    var color = "";
    for (let i = 0, j = palette.values.length; i < j; i++) {
      if (palette.values[i].name === shade) {
        color = palette.values[i].baseColor;
        break;
      }
    }

    return color;
  }


}

